import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { fetchStylistsList } from '@/lib/api/stylists'
import { cn } from '@/lib/utils'

/**
 * Clock in/out + active attendance sessions for staff.
 */
export function StaffAttendancePanel() {
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState('')
  const [stylists, setStylists] = useState([])
  const [sessions, setSessions] = useState([])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [staff, attendance] = await Promise.all([
        fetchStylistsList({ is_active: 1 }),
        api.get('/masters/stylist-attendance'),
      ])
      setStylists(Array.isArray(staff) ? staff : [])
      setSessions(attendance.data?.data?.sessions ?? [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load attendance.'))
      setStylists([])
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const sessionByStylist = useMemo(() => {
    const map = new Map()
    for (const session of sessions) {
      const stylistId = Number(session.stylist_id ?? session.stylist?.id)
      if (Number.isFinite(stylistId)) {
        map.set(stylistId, session)
      }
    }
    return map
  }, [sessions])

  async function handleClock(stylistId, action) {
    setBusyId(stylistId)
    setError('')
    try {
      await api.post(`/masters/stylists/${stylistId}/${action}`)
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to ${action.replace('-', ' ')}.`))
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading attendance…
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
        {stylists.length === 0 ? (
          <li className="p-4 text-sm text-muted-foreground">No active staff members.</li>
        ) : (
          stylists.map((stylist) => {
            const session = sessionByStylist.get(Number(stylist.id))
            const clockedIn = Boolean(session && !session.clocked_out_at)

            return (
              <li key={stylist.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="font-medium">{stylist.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {clockedIn
                      ? `Clocked in${session?.clocked_in_at ? ` · ${new Date(session.clocked_in_at).toLocaleTimeString()}` : ''}`
                      : 'Not clocked in'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={clockedIn ? 'outline' : 'default'}
                    disabled={busyId === stylist.id || clockedIn}
                    className={cn(!clockedIn && 'min-w-24')}
                    onClick={() => handleClock(stylist.id, 'clock-in')}
                  >
                    Clock in
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busyId === stylist.id || !clockedIn}
                    onClick={() => handleClock(stylist.id, 'clock-out')}
                  >
                    Clock out
                  </Button>
                </div>
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}
